import { useState, useEffect, useCallback } from 'react'
import {
  Newspaper, Plus, Search, Edit2, Trash2, Eye, Calendar,
  Image as ImageIcon, MessageSquare, Upload, X,
  ChevronLeft, ChevronRight, User, ThumbsUp, CheckCircle, XCircle,
  Clock, FileText, Users, Pin, Lock, Mail, Phone, Building2,
  Send, Check, AlertTriangle, RotateCcw, RefreshCw, Handshake, Star
} from 'lucide-react'
import { contentApi, publicContentApi } from '@/services/api'
import { toast } from 'sonner'

const API_BASE = (import.meta as any).env?.VITE_API_URL || '/api/v1'

// ==================== TYPES ====================
interface NewsItem {
  id: number
  title?: string
  title_fr?: string
  title_en?: string
  content?: string
  content_fr?: string
  content_en?: string
  excerpt?: string
  excerpt_fr?: string
  excerpt_en?: string
  summary_fr?: string
  summary_en?: string
  category?: string
  image_url?: string
  status?: string
  is_published?: boolean
  is_featured?: boolean
  view_count?: number
  views?: number
  like_count?: number
  likes?: number
  author_name?: string
  created_at: string
  published_at?: string
}

interface EventItem {
  id: number
  title?: string
  title_fr?: string
  title_en?: string
  description?: string
  description_fr?: string
  description_en?: string
  event_type?: string
  category?: string
  start_date?: string
  event_date?: string
  location?: string
  image_url?: string
  status?: string
  is_public?: boolean
  attendees_count?: number
  created_at: string
}

interface GalleryImage {
  id: number
  title?: string
  category?: string
  caption_fr?: string
  caption_en?: string
  description?: string
  image_url?: string
  file_path?: string
  uploaded_by_name?: string
  created_at: string
}

interface ForumTopic {
  id: number
  title: string
  content: string
  category?: string
  author_name?: string
  reply_count?: number
  replies_count?: number
  views?: number
  view_count?: number
  is_pinned?: boolean
  is_locked?: boolean
  status?: string
  created_at: string
}

interface ForumReply {
  id: number
  content: string
  author_name?: string
  guest_name?: string
  created_at: string
}

interface TrustedPartner {
  id: number
  name: string
  city?: string
  quote_fr?: string
  quote_en?: string
  initials?: string
  color?: string
  is_active?: boolean
  display_order?: number
}

interface DemoRequest {
  id: number
  full_name: string
  email: string
  school_name?: string
  phone?: string
  message?: string
  status: 'pending' | 'contacted' | 'completed' | 'cancelled'
  created_at: string
  updated_at?: string
}

// ==================== HELPERS ====================
/* Token JWT robuste : évite 'Bearer null' (401 'Not enough segments') */
function getAuthToken(): string | null {
  const isValidJwt = (v: unknown): v is string =>
    typeof v === 'string' && v.split('.').length === 3 && v.startsWith('eyJ')
  for (const key of ['auth_token', 'token', 'access_token', 'authToken', 'accessToken']) {
    const raw = localStorage.getItem(key)
    if (!raw) continue
    try {
      const parsed = JSON.parse(raw)
      if (isValidJwt(parsed)) return parsed
    } catch { /* not JSON */ }
    const clean = raw.replace(/^"|"$/g, '').trim()
    if (isValidJwt(clean)) return clean
  }
  for (const key of Object.keys(localStorage)) {
    if (!/auth|user|session|token/i.test(key)) continue
    try {
      const blob = JSON.parse(localStorage.getItem(key) || '')
      const candidates = [blob?.state?.token, blob?.state?.auth?.token, blob?.state?.access_token, blob?.token]
      for (const c of candidates) if (isValidJwt(c)) return c
    } catch { /* not JSON */ }
  }
  return null
}

/* Images : /uploads/... → http://localhost:8001/uploads/... */
const API_ORIGIN = ((import.meta.env.VITE_API_URL as string | undefined) || '').replace(/\/api\/v1\/?$/, '')
  || 'http://localhost:8001'
const resolveImg = (url?: string): string => {
  if (!url) return ''
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url
  return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`
}

const trTitle = (n: { title_fr?: string; title_en?: string; title?: string }, lang: 'fr' | 'en' = 'fr') =>
  lang === 'fr' ? (n.title_fr || n.title || '') : (n.title_en || n.title_fr || n.title || '')
const trText = (n: any, base: string, lang: 'fr' | 'en' = 'fr') =>
  lang === 'fr' ? (n[`${base}_fr`] || n[base] || '') : (n[`${base}_en`] || n[`${base}_fr`] || n[base] || '')
const isPub = (n: any) => (n.status ? String(n.status).toLowerCase() === 'published' : !!n.is_published)
const numViews = (n: any) => n.view_count ?? n.views ?? 0
const numLikes = (n: any) => n.like_count ?? n.likes ?? 0
const numReplies = (t: any) => t.reply_count ?? t.replies_count ?? 0

// Valeurs exactes attendues par le backend (enum : education/policy/exam/results/event/official)
const NEWS_CATEGORIES = ['education', 'exam', 'official', 'event', 'results', 'policy']

// Libellés français pour l'affichage (y compris anciennes catégories legacy)
const NEWS_CATEGORY_LABELS: Record<string, string> = {
  education: 'Éducation', exam: 'Examens', official: 'Communiqué officiel',
  event: 'Événements', results: 'Résultats', policy: 'Administration',
  'Général': 'Général', 'Académique': 'Académique', 'Sport': 'Sport',
  'Culture': 'Culture', 'Administration': 'Administration', 'Urgence': 'Urgence',
}
// Anciennes catégories françaises -> valeurs enum valides du backend
const LEGACY_CAT_MAP: Record<string, string> = {
  'général': 'education', 'general': 'education', 'académique': 'exam',
  'sport': 'event', 'culture': 'event', 'administration': 'policy',
  'urgence': 'official',
}
const normalizeCategory = (c?: string): string => {
  if (!c) return 'education'
  if (NEWS_CATEGORIES.includes(c)) return c
  return LEGACY_CAT_MAP[c.toLowerCase()] || 'education'
}

const catLabel = (c?: string) => (c ? NEWS_CATEGORY_LABELS[c] || NEWS_CATEGORY_LABELS[c.toLowerCase()] || c : '—')
const EVENT_TYPES = ['Cérémonie', 'Sport', 'Culture', 'Réunion', 'Formation', 'Autre']
const GALLERY_CATEGORIES = ['Événements', 'Classes', 'Sports', 'Vie scolaire', 'Infrastructure']

const DEMO_STATUS_LABELS: Record<string, { fr: string; color: string }> = {
  pending: { fr: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  contacted: { fr: 'Contacté', color: 'bg-blue-100 text-blue-700' },
  completed: { fr: 'Terminé', color: 'bg-green-100 text-green-700' },
  cancelled: { fr: 'Annulé', color: 'bg-red-100 text-red-700' },
}

export default function ContentPage() {
  const [activeTab, setActiveTab] = useState<'news' | 'events' | 'gallery' | 'forum' | 'demos' | 'partners'>('news')
  const [loading, setLoading] = useState(true)

  // News
  const [news, setNews] = useState<NewsItem[]>([])
  const [newsPage, setNewsPage] = useState(1)
  const [newsTotal, setNewsTotal] = useState(0)
  const [newsSearch, setNewsSearch] = useState('')

  // Events
  const [events, setEvents] = useState<EventItem[]>([])
  const [eventsPage, setEventsPage] = useState(1)
  const [eventsTotal, setEventsTotal] = useState(0)

  // Gallery
  const [images, setImages] = useState<GalleryImage[]>([])
  const [galleryPage, setGalleryPage] = useState(1)
  const [galleryTotal, setGalleryTotal] = useState(0)

  // Forum
  const [topics, setTopics] = useState<ForumTopic[]>([])
  const [forumPage, setForumPage] = useState(1)
  const [forumTotal, setForumTotal] = useState(0)

  // Demos
  const [demos, setDemos] = useState<DemoRequest[]>([])
  const [demosPage, setDemosPage] = useState(1)
  const [demosTotal, setDemosTotal] = useState(0)
  const [demosLoading, setDemosLoading] = useState(false)
  const [demosError, setDemosError] = useState<string | null>(null)
  const [demosSearch, setDemosSearch] = useState('')

  // Partners (Ils nous font confiance)
  const [partners, setPartners] = useState<TrustedPartner[]>([])
  const [partnersLoading, setPartnersLoading] = useState(false)
  const [showPartnerForm, setShowPartnerForm] = useState(false)
  const [editingPartner, setEditingPartner] = useState<TrustedPartner | null>(null)

  // Modals
  const [showNewsForm, setShowNewsForm] = useState(false)
  const [showEventForm, setShowEventForm] = useState(false)
  const [showImageUpload, setShowImageUpload] = useState(false)
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null)
  const [editingEvent, setEditingEvent] = useState<EventItem | null>(null)
  const [selectedTopic, setSelectedTopic] = useState<ForumTopic | null>(null)
  const [topicReplies, setTopicReplies] = useState<ForumReply[]>([])
  const [showTopicDetail, setShowTopicDetail] = useState(false)
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null)

  // Popup de confirmation de suppression (remplace confirm() natif)
  const [confirmDelete, setConfirmDelete] = useState<{
    title: string
    message: string
    action: () => Promise<void>
  } | null>(null)

  const perPage = 12

  const extractItems = (res: any): { items: any[]; total: number } => {
    if (Array.isArray(res)) return { items: res, total: res.length }
    return { items: res?.items || [], total: res?.total || 0 }
  }

  const loadNews = useCallback(async () => {
    try {
      const res = await contentApi.getNews({ page: newsPage, per_page: perPage, search: newsSearch || undefined })
      const { items, total } = extractItems(res)
      setNews(items)
      setNewsTotal(total)
    } catch (err) {
      console.error('Error loading news:', err)
    }
  }, [newsPage, newsSearch])

  const loadEvents = useCallback(async () => {
    try {
      const res = await contentApi.getEvents({ page: eventsPage, per_page: perPage })
      const { items, total } = extractItems(res)
      setEvents(items)
      setEventsTotal(total)
    } catch (err) {
      console.error('Error loading events:', err)
    }
  }, [eventsPage])

  const loadGallery = useCallback(async () => {
    try {
      const res = await contentApi.getGallery({ page: galleryPage, per_page: perPage })
      const { items, total } = extractItems(res)
      setImages(items)
      setGalleryTotal(total)
    } catch (err) {
      console.error('Error loading gallery:', err)
    }
  }, [galleryPage])

  const loadForum = useCallback(async () => {
    try {
      const res = await contentApi.getForumTopics({ page: forumPage, per_page: perPage })
      const { items, total } = extractItems(res)
      setTopics(items)
      setForumTotal(total)
    } catch (err) {
      console.error('Error loading forum:', err)
    }
  }, [forumPage])

  // ═══════════════════════════════════════════════════════════════
  // CHARGEMENT DES DEMOS — CORRIGÉ
  // ═══════════════════════════════════════════════════════════════
  const loadDemos = useCallback(async () => {
    setDemosLoading(true)
    setDemosError(null)
    try {

      const params = new URLSearchParams({ page: String(demosPage), per_page: String(perPage) })
      if (demosSearch) params.append('search', demosSearch)

      const res = await fetch(`${API_BASE}/demo-requests?${params}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!res.ok) {
        const errText = await res.text()
        console.error('[Demos] HTTP error:', res.status, errText)
        setDemosError(`Erreur ${res.status}: ${errText}`)
        setDemos([])
        setDemosTotal(0)
        return
      }

      const data = await res.json()
      console.log('[Demos] Response:', data)

      const items = data.items || data.data || data.results || []
      const total = data.total ?? data.count ?? items.length

      setDemos(items)
      setDemosTotal(total)
    } catch (err: any) {
      console.error('[Demos] Error:', err)
      setDemosError(err.message || 'Erreur de chargement')
      setDemos([])
      setDemosTotal(0)
    } finally {
      setDemosLoading(false)
    }
  }, [demosPage, demosSearch])

  const loadPartners = useCallback(async () => {
    setPartnersLoading(true)
    try {
      const token = getAuthToken()
      const res = await fetch(`${API_BASE}/content/partners`, {
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`Erreur ${res.status}`)
      const data = await res.json()
      setPartners(data.items || [])
    } catch (err) {
      console.error('[Partners] Error:', err)
      toast.error('Erreur de chargement des partenaires')
    } finally {
      setPartnersLoading(false)
    }
  }, [])

  const handleDeletePartner = (id: number) => {
    setConfirmDelete({
      title: 'Retirer cet établissement ?',
      message: "Il ne sera plus affiché dans la section « Ils nous font confiance » de la page d'accueil.",
      action: async () => {
        try {
      const token = getAuthToken()
      const res = await fetch(`${API_BASE}/content/partners/${id}`, {
      credentials: 'include',        // ← SEULE ligne ajoutée
        method: 'DELETE',
      })
          if (res.ok) {
            toast.success('Établissement retiré')
            loadPartners()
          } else {
            toast.error('Erreur lors de la suppression')
          }
        } catch { toast.error('Erreur réseau') }
      },
    })
  }

  const handleTogglePartnerActive = async (p: TrustedPartner) => {
    try {
      const token = getAuthToken()
      const res = await fetch(`${API_BASE}/content/partners/${p.id}`, {
        credentials: 'include',        // ← SEULE ligne ajoutée
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...p, is_active: !p.is_active }),
      })
      if (res.ok) { toast.success(p.is_active ? 'Masqué du site' : 'Affiché sur le site'); loadPartners() }
      else toast.error('Erreur')
    } catch { toast.error('Erreur réseau') }
  }

  // Chargement initial des onglets classiques
  useEffect(() => {
    setLoading(true)
    Promise.all([loadNews(), loadEvents(), loadGallery(), loadForum()]).finally(() => setLoading(false))
  }, [loadNews, loadEvents, loadGallery, loadForum])

  // Chargement des démos quand onglet sélectionné ou page/recherche change
  useEffect(() => {
    if (activeTab === 'demos') {
      loadDemos()
    }
  }, [activeTab, demosPage, demosSearch])

  useEffect(() => {
    if (activeTab === 'partners') {
      loadPartners()
    }
  }, [activeTab])

  // ==================== ACTIONS ====================
  const handleDeleteNews = (id: number) => {
    setConfirmDelete({
      title: 'Supprimer cette actualité ?',
      message: 'Cette action est définitive et ne peut pas être annulée.',
      action: async () => {
        try {
          await contentApi.deleteNews(id)
          toast.success('Actualité supprimée')
          loadNews()
        } catch { toast.error('Erreur') }
      },
    })
  }

  const handleDeleteEvent = (id: number) => {
    setConfirmDelete({
      title: 'Supprimer cet événement ?',
      message: 'Cette action est définitive et ne peut pas être annulée.',
      action: async () => {
        try {
          await contentApi.deleteEvent(id)
          toast.success('Événement supprimé')
          loadEvents()
        } catch { toast.error('Erreur') }
      },
    })
  }

  const handleDeleteImage = (id: number) => {
    setConfirmDelete({
      title: 'Supprimer cette image ?',
      message: 'Le fichier sera également supprimé du serveur.',
      action: async () => {
        try {
          await contentApi.deleteImage(id)
          toast.success('Image supprimée')
          loadGallery()
        } catch { toast.error('Erreur') }
      },
    })
  }

  const handlePublishToggle = async (item: NewsItem) => {
    try {
      const nowPublished = !isPub(item)
      await contentApi.updateNews(item.id, {
        status: nowPublished ? 'published' : 'draft',
        is_published: nowPublished,
      } as any)
      toast.success(nowPublished ? 'Publié sur le site' : 'Dépublié')
      loadNews()
    } catch { toast.error('Erreur') }
  }

  const openTopicDetail = async (topic: ForumTopic) => {
    setSelectedTopic(topic)
    setShowTopicDetail(true)
    setTopicReplies([])
    try {
      const res = await contentApi.getForumTopic(topic.id)
      setTopicReplies(res?.replies || [])
    } catch { /* ignore */ }
  }

  const handleTogglePin = async (topic: ForumTopic) => {
    try {
      await publicContentApi.updateForumTopic(topic.id, { is_pinned: !topic.is_pinned } as any)
      toast.success(topic.is_pinned ? 'Désépinglé' : 'Épinglé')
      loadForum()
    } catch { toast.error('Erreur') }
  }

  const handleToggleLock = async (topic: ForumTopic) => {
    try {
      await publicContentApi.updateForumTopic(topic.id, { is_locked: !topic.is_locked } as any)
      toast.success(topic.is_locked ? 'Déverrouillé' : 'Verrouillé')
      loadForum()
    } catch { toast.error('Erreur') }
  }

  const handleDeleteTopic = (id: number) => {
    setConfirmDelete({
      title: 'Supprimer ce sujet ?',
      message: 'Toutes les réponses associées seront également supprimées.',
      action: async () => {
        try {
          await contentApi.deleteForumTopic(id)
          toast.success('Sujet supprimé')
          loadForum()
        } catch { toast.error('Erreur') }
      },
    })
  }

  const handleUpdateDemoStatus = async (id: number, status: string) => {
    try {
      const token = getAuthToken()
      const res = await fetch(`${API_BASE}/demo-requests/${id}/status`, {
        credentials: 'include',        // ← SEULE ligne ajoutée
	method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status })
      })
      if (res.ok) {
        toast.success('Statut mis à jour')
        loadDemos()
      } else {
        const err = await res.text()
        toast.error(`Erreur: ${err}`)
      }
    } catch (e: any) {
      toast.error(`Erreur réseau: ${e.message}`)
    }
  }

  const handleDeleteDemo = (id: number) => {
    setConfirmDelete({
      title: 'Supprimer cette demande ?',
      message: 'Cette action est définitive et ne peut pas être annulée.',
      action: async () => {
        try {
      const token = getAuthToken()
      const res = await fetch(`${API_BASE}/demo-requests/${id}`, {
        credentials: 'include',
        method: 'DELETE',
      })
          if (res.ok) {
            toast.success('Demande supprimée')
            loadDemos()
          } else {
            toast.error('Erreur lors de la suppression')
          }
        } catch {
          toast.error('Erreur réseau')
        }
      },
    })
  }

  const getCategoryColor = (category?: string) => {
    const colors: Record<string, string> = {
      education: 'bg-blue-100 text-blue-700', exam: 'bg-blue-100 text-blue-700',
      official: 'bg-amber-100 text-amber-700', event: 'bg-purple-100 text-purple-700',
      results: 'bg-green-100 text-green-700', policy: 'bg-gray-100 text-gray-700',
      'Urgence': 'bg-red-100 text-red-700', 'Académique': 'bg-blue-100 text-blue-700',
      'Sport': 'bg-green-100 text-green-700', 'Culture': 'bg-purple-100 text-purple-700',
      'Administration': 'bg-gray-100 text-gray-700', 'Général': 'bg-blue-100 text-blue-700',
    }
    return colors[(category || '').toLowerCase()] || colors[category || ''] || 'bg-gray-100 text-gray-700'
  }

  const Pagination = ({ page, total, onPage }: { page: number; total: number; onPage: (p: number) => void }) =>
    total > perPage ? (
      <div className="flex justify-center gap-2">
        <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page === 1}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="px-3 py-1.5 text-sm text-gray-600">Page {page} / {Math.ceil(total / perPage)}</span>
        <button onClick={() => onPage(page + 1)} disabled={page * perPage >= total}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    ) : null

  if (loading && activeTab !== 'demos') {
    return <div className="p-8 text-center text-gray-500">Chargement du contenu...</div>
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-3">
              <Newspaper className="h-7 w-7 text-primary-600" />
              Gestion du Contenu
            </h1>
            <p className="page-subtitle">
              Publiez ici — le contenu apparaît immédiatement sur le site public
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 mt-4 border-b border-gray-200 overflow-x-auto">
          {[
            { id: 'news', label: 'Actualités', icon: Newspaper },
            { id: 'events', label: 'Événements', icon: Calendar },
            { id: 'gallery', label: 'Galerie', icon: ImageIcon },
            { id: 'forum', label: 'Forum', icon: MessageSquare },
            { id: 'demos', label: 'Demandes de démo', icon: Send },
            { id: 'partners', label: 'Ils nous font confiance', icon: Handshake },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'demos' && demosTotal > 0 && (
                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full">
                  {demosTotal}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ==================== NEWS TAB ==================== */}
      {activeTab === 'news' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input value={newsSearch} onChange={(e) => { setNewsSearch(e.target.value); setNewsPage(1) }}
                placeholder="Rechercher..." className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <button onClick={() => { setEditingNews(null); setShowNewsForm(true) }} className="btn-primary">
              <Plus className="mr-2 h-4 w-4" />Nouvelle actualité
            </button>
          </div>

          {news.length === 0 ? (
            <p className="text-center text-gray-400 py-10">Aucune actualité</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.map((item) => (
                <div key={item.id} className="card overflow-hidden group">
                  {item.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img src={resolveImg(item.image_url)} alt={trTitle(item)} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(item.category)}`}>{catLabel(item.category)}</span>
                      {!isPub(item) && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Brouillon</span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{trTitle(item)}</h3>
                    <p className="text-sm text-gray-500 line-clamp-3 mb-3">{trText(item, 'excerpt') || trText(item, 'content')}</p>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <div className="flex items-center gap-1"><User className="w-3 h-3" />{item.author_name || '—'}</div>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{numViews(item)}</span>
                        <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" />{numLikes(item)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                      <button onClick={() => handlePublishToggle(item)}
                        className={`p-1.5 rounded-lg ${isPub(item) ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                        title={isPub(item) ? 'Dépublier' : 'Publier sur le site'}>
                        {isPub(item) ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      </button>
                      <button onClick={() => { setEditingNews(item); setShowNewsForm(true) }}
                        className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500" title="Modifier">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteNews(item.id)}
                        className="p-1.5 hover:bg-red-50 rounded-lg text-red-600" title="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pagination page={newsPage} total={newsTotal} onPage={setNewsPage} />
        </div>
      )}

      {/* ==================== EVENTS TAB ==================== */}
      {activeTab === 'events' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Événements</h2>
            <button onClick={() => { setEditingEvent(null); setShowEventForm(true) }} className="btn-primary">
              <Plus className="mr-2 h-4 w-4" />Nouvel événement
            </button>
          </div>

          <div className="space-y-4">
            {events.map((event) => {
              const dateStr = event.start_date || event.event_date || ''
              return (
                <div key={event.id} className="card">
                  <div className="card-body flex gap-4">
                    {event.image_url && (
                      <div className="w-32 h-32 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={resolveImg(event.image_url)} alt={trTitle(event)} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(event.event_type || event.category)}`}>
                              {event.event_type || event.category}
                            </span>
                            {event.is_public && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Public</span>
                            )}
                            {event.status && event.status !== 'published' && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">{event.status}</span>
                            )}
                          </div>
                          <h3 className="font-semibold text-lg text-gray-900">{trTitle(event)}</h3>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => { setEditingEvent(event); setShowEventForm(true) }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><Edit2 className="w-4 h-4" /></button>
                          <button onClick={() => handleDeleteEvent(event.id)}
                            className="p-1.5 hover:bg-red-50 rounded-lg text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">{trText(event, 'description')}</p>
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{dateStr ? new Date(dateStr).toLocaleDateString('fr-FR') : '—'}</span>
                        {event.location && <span className="flex items-center gap-1"><FileText className="w-4 h-4" />{event.location}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
            {events.length === 0 && <p className="text-center text-gray-400 py-10">Aucun événement</p>}
          </div>
          <Pagination page={eventsPage} total={eventsTotal} onPage={setEventsPage} />
        </div>
      )}

      {/* ==================== GALLERY TAB ==================== */}
      {activeTab === 'gallery' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Galerie photos</h2>
            <button onClick={() => setShowImageUpload(true)} className="btn-primary">
              <Upload className="mr-2 h-4 w-4" />Ajouter photos
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image) => (
              <div key={image.id}
                className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer"
                onClick={() => setSelectedImage(image)}>
                <img src={resolveImg(image.image_url || image.file_path)} alt={image.title || image.caption_fr}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors" />
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-white text-sm font-medium">{image.title || image.caption_fr}</p>
                  <p className="text-white/80 text-xs">{image.category}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); handleDeleteImage(image.id) }}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          {images.length === 0 && <p className="text-center text-gray-400 py-10">Galerie vide — uploadez des photos</p>}
          <Pagination page={galleryPage} total={galleryTotal} onPage={setGalleryPage} />
        </div>
      )}

      {/* ==================== FORUM TAB ==================== */}
      {activeTab === 'forum' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Modération du forum</h2>
          </div>

          <div className="space-y-3">
            {topics.map((topic) => (
              <div key={topic.id}
                className={`card ${topic.is_pinned ? 'border-l-4 border-l-primary-500' : ''}`}>
                <div className="card-body py-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1 cursor-pointer" onClick={() => openTopicDetail(topic)}>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {topic.is_pinned && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 flex items-center gap-1"><Pin className="w-3 h-3" />Épinglé</span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(topic.category)}`}>{topic.category}</span>
                        {topic.is_locked && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1"><Lock className="w-3 h-3" />Verrouillé</span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900">{topic.title}</h3>
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{topic.content}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{topic.author_name || '—'}</span>
                        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{numReplies(topic)} réponses</span>
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{topic.view_count ?? topic.views ?? 0} vues</span>
                        <span>{new Date(topic.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => handleTogglePin(topic)}
                        className={`p-1.5 rounded-lg ${topic.is_pinned ? 'text-primary-600 hover:bg-primary-50' : 'text-gray-400 hover:bg-gray-50'}`}
                        title={topic.is_pinned ? 'Désépingler' : 'Épingler'}>
                        <Pin className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleToggleLock(topic)}
                        className={`p-1.5 rounded-lg ${topic.is_locked ? 'text-red-600 hover:bg-red-50' : 'text-gray-400 hover:bg-gray-50'}`}
                        title={topic.is_locked ? 'Déverrouiller' : 'Verrouiller'}>
                        <Lock className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteTopic(topic.id)}
                        className="p-1.5 hover:bg-red-50 rounded-lg text-red-600" title="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {topics.length === 0 && <p className="text-center text-gray-400 py-10">Aucun sujet</p>}
          </div>
          <Pagination page={forumPage} total={forumTotal} onPage={setForumPage} />
        </div>
      )}

      {/* ==================== DEMOS TAB — CORRIGÉ ==================== */}
      {activeTab === 'demos' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={demosSearch}
                onChange={(e) => { setDemosSearch(e.target.value); setDemosPage(1) }}
                placeholder="Rechercher une demande..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadDemos}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 transition-colors"
                title="Actualiser"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                {demos.filter(d => d.status === 'pending').length} en attente
              </span>
            </div>
          </div>

          {demosLoading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-gray-500">Chargement des demandes...</p>
            </div>
          ) : demosError ? (
            <div className="p-8 text-center">
              <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
              <p className="text-red-600 font-medium">{demosError}</p>
              <button onClick={loadDemos} className="mt-3 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors">
                Réessayer
              </button>
            </div>
          ) : demos.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <Send className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Aucune demande de démonstration</p>
              <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">
                Les demandes apparaissent ici quand un visiteur remplit le formulaire sur la page d'accueil.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Nom</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Email</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Établissement</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Téléphone</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Message</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Statut</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {demos.map((demo) => {
                      const statusInfo = DEMO_STATUS_LABELS[demo.status] || { fr: demo.status, color: 'bg-gray-100 text-gray-700' }
                      return (
                        <tr key={demo.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-blue-600" />
                              </div>
                              <span className="font-medium text-gray-900">{demo.full_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <a href={`mailto:${demo.email}`} className="text-blue-600 hover:underline flex items-center gap-1 text-xs">
                              <Mail className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate max-w-[120px]">{demo.email}</span>
                            </a>
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-xs">
                            {demo.school_name ? (
                              <span className="flex items-center gap-1"><Building2 className="w-3 h-3 flex-shrink-0" />{demo.school_name}</span>
                            ) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-xs">
                            {demo.phone ? (
                              <span className="flex items-center gap-1"><Phone className="w-3 h-3 flex-shrink-0" />{demo.phone}</span>
                            ) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-600 max-w-[200px]">
                            <p className="truncate text-xs" title={demo.message || ''}>{demo.message || <span className="text-gray-400">—</span>}</p>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={demo.status}
                              onChange={(e) => handleUpdateDemoStatus(demo.id, e.target.value)}
                              className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${statusInfo.color}`}
                            >
                              <option value="pending">En attente</option>
                              <option value="contacted">Contacté</option>
                              <option value="completed">Terminé</option>
                              <option value="cancelled">Annulé</option>
                            </select>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                            {new Date(demo.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleDeleteDemo(demo.id)}
                              className="p-1.5 hover:bg-red-50 rounded-lg text-red-600 transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <Pagination page={demosPage} total={demosTotal} onPage={setDemosPage} />
        </div>
      )}

      {/* ==================== PARTNERS TAB ==================== */}
      {activeTab === 'partners' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold">Ils nous font confiance</h2>
              <p className="text-sm text-gray-500">Établissements affichés sur la page d'accueil (section marketing)</p>
            </div>
            <button onClick={() => { setEditingPartner(null); setShowPartnerForm(true) }} className="btn-primary">
              <Plus className="mr-2 h-4 w-4" />Ajouter un établissement
            </button>
          </div>

          {partnersLoading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-gray-500">Chargement...</p>
            </div>
          ) : partners.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <Handshake className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Aucun établissement</p>
              <p className="text-sm text-gray-400 mt-1">Ajoutez les établissements qui apparaîtront dans « Ils nous font confiance »</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {partners.map((p) => (
                <div key={p.id} className={`card p-5 ${p.is_active === false ? 'opacity-60' : ''}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-12 h-12 ${p.color || 'bg-blue-600'} rounded-xl flex items-center justify-center shrink-0`}>
                      <span className="text-white font-black">{p.initials || p.name?.slice(0, 2).toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 truncate">{p.name}</h3>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                        {p.city || '—'}
                      </p>
                    </div>
                    {p.is_active === false && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-[10px] font-bold rounded-full">MASQUÉ</span>
                    )}
                  </div>
                  {p.quote_fr && (
                    <p className="text-sm text-gray-600 italic line-clamp-2 mb-3">“{p.quote_fr}”</p>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400">Ordre : {p.display_order ?? 0}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleTogglePartnerActive(p)}
                        className={`p-1.5 rounded-lg ${p.is_active !== false ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                        title={p.is_active !== false ? 'Masquer du site' : 'Afficher sur le site'}>
                        {p.is_active !== false ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      </button>
                      <button onClick={() => { setEditingPartner(p); setShowPartnerForm(true) }}
                        className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500" title="Modifier">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeletePartner(p.id)}
                        className="p-1.5 hover:bg-red-50 rounded-lg text-red-600" title="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==================== POPUP CONFIRMATION SUPPRESSION ==================== */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmDelete(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-fade-in">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-7 h-7 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">{confirmDelete.title}</h3>
            <p className="text-sm text-gray-500 text-center mb-6">{confirmDelete.message}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  const action = confirmDelete.action
                  setConfirmDelete(null)
                  await action()
                }}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== MODALS ==================== */}
      {showNewsForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowNewsForm(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editingNews ? "Modifier l'actualité" : 'Nouvelle actualité'}</h3>
              <button onClick={() => setShowNewsForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <NewsForm initialData={editingNews} onCancel={() => setShowNewsForm(false)}
              onSubmit={async (data) => {
                try {
                  // Envoi des deux conventions pour compatibilité schéma
                  const payload = {
                    ...data,
                    category: normalizeCategory(data.category),
                    summary_fr: data.excerpt_fr,
                    summary_en: data.excerpt_en,
                  }
                  if (editingNews) {
                    await contentApi.updateNews(editingNews.id, payload)
                    toast.success('Actualité mise à jour')
                  } else {
                    await contentApi.createNews(payload)
                    toast.success('Actualité créée — publiée sur le site si statut = Publié')
                  }
                  setShowNewsForm(false)
                  loadNews()
                } catch { toast.error('Erreur') }
              }} />
          </div>
        </div>
      )}

      {showEventForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowEventForm(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editingEvent ? "Modifier l'événement" : 'Nouvel événement'}</h3>
              <button onClick={() => setShowEventForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <EventForm initialData={editingEvent} onCancel={() => setShowEventForm(false)}
              onSubmit={async (data) => {
                try {
                  if (editingEvent) {
                    await contentApi.updateEvent(editingEvent.id, data)
                    toast.success('Événement mis à jour')
                  } else {
                    await contentApi.createEvent(data)
                    toast.success('Événement créé')
                  }
                  setShowEventForm(false)
                  loadEvents()
                } catch { toast.error('Erreur') }
              }} />
          </div>
        </div>
      )}

      {showImageUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowImageUpload(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Ajouter des photos</h3>
              <button onClick={() => setShowImageUpload(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <ImageUploadForm onCancel={() => setShowImageUpload(false)}
              onSubmit={async (formData) => {
                try {
                  await contentApi.uploadImage(formData)
                  toast.success('Image publiée dans la galerie')
                  setShowImageUpload(false)
                  loadGallery()
                } catch { toast.error("Erreur d'upload") }
              }} />
          </div>
        </div>
      )}

      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setSelectedImage(null)}>
          <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img src={resolveImg(selectedImage.image_url || selectedImage.file_path)} alt={selectedImage.title}
              className="max-w-full max-h-[85vh] rounded-lg" />
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent rounded-b-lg">
              <p className="text-white font-medium">{selectedImage.title}</p>
              <p className="text-white/80 text-sm">{selectedImage.description}</p>
            </div>
            <button onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-lg hover:bg-black/70"><X className="w-5 h-5" /></button>
          </div>
        </div>
      )}

      {showTopicDetail && selectedTopic && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowTopicDetail(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold">{selectedTopic.title}</h3>
              <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                <User className="w-4 h-4" />{selectedTopic.author_name || '—'}<span>•</span>
                {new Date(selectedTopic.created_at).toLocaleDateString('fr-FR')}
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700">{selectedTopic.content}</p>
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h4 className="font-medium mb-4">Réponses ({topicReplies.length})</h4>
                <div className="space-y-4">
                  {topicReplies.length === 0 && <p className="text-sm text-gray-400">Aucune réponse</p>}
                  {topicReplies.map((reply) => (
                    <div key={reply.id} className="flex gap-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="flex-1 bg-gray-50 rounded-lg p-3">
                        <p className="text-sm font-medium">{reply.author_name || reply.guest_name || 'Invité'}</p>
                        <p className="text-sm text-gray-600 mt-1">{reply.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPartnerForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPartnerForm(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Handshake className="w-5 h-5 text-primary-600" />
                {editingPartner ? "Modifier l'établissement" : "Ajouter un établissement"}
              </h3>
              <button onClick={() => setShowPartnerForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <PartnerForm
              initialData={editingPartner}
              onCancel={() => setShowPartnerForm(false)}
              onSubmit={async (data) => {
                try {
                  const token = getAuthToken()
                  const url = editingPartner
                    ? `${API_BASE}/content/partners/${editingPartner.id}`
                    : `${API_BASE}/content/partners`
                  const res = await fetch(url, {
                    method: editingPartner ? 'PUT' : 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data),
                  })
                  if (res.ok) {
                    toast.success(editingPartner ? 'Établissement mis à jour' : "Établissement ajouté — visible sur la page d'accueil")
                    setShowPartnerForm(false)
                    loadPartners()
                  } else {
                    const err = await res.json().catch(() => ({}))
                    toast.error(err.detail || 'Erreur')
                  }
                } catch { toast.error('Erreur réseau') }
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const PARTNER_COLORS = [
  'bg-blue-600', 'bg-amber-500', 'bg-emerald-600', 'bg-violet-600',
  'bg-rose-600', 'bg-cyan-600', 'bg-indigo-600', 'bg-slate-600',
]

function PartnerForm({ initialData, onSubmit, onCancel }: {
  initialData?: TrustedPartner | null
  onSubmit: (data: any) => void
  onCancel: () => void
}) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    city: initialData?.city || '',
    quote_fr: initialData?.quote_fr || '',
    quote_en: initialData?.quote_en || '',
    initials: initialData?.initials || '',
    color: initialData?.color || 'bg-blue-600',
    display_order: initialData?.display_order ?? 0,
    is_active: initialData?.is_active ?? true,
  })

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData) }} className="p-6 space-y-4">
      <div>
        <label className="label">Nom de l'établissement *</label>
        <input required value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input" placeholder="Ex : Lycée Bilingue de Baleng" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Ville *</label>
          <input required value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            className="input" placeholder="Ex : Bafoussam" />
        </div>
        <div>
          <label className="label">Initiales (badge)</label>
          <input value={formData.initials} maxLength={3}
            onChange={(e) => setFormData({ ...formData, initials: e.target.value.toUpperCase() })}
            className="input" placeholder="Ex : LB" />
        </div>
      </div>
      <div>
        <label className="label">Témoignage (FR)</label>
        <textarea rows={2} value={formData.quote_fr}
          onChange={(e) => setFormData({ ...formData, quote_fr: e.target.value })}
          className="input" placeholder="Ex : Une gestion simplifiée de A à Z..." />
      </div>
      <div>
        <label className="label">Témoignage (EN)</label>
        <textarea rows={2} value={formData.quote_en}
          onChange={(e) => setFormData({ ...formData, quote_en: e.target.value })}
          className="input" placeholder="English quote..." />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Couleur du badge</label>
          <div className="flex flex-wrap gap-2">
            {PARTNER_COLORS.map((c) => (
              <button key={c} type="button"
                onClick={() => setFormData({ ...formData, color: c })}
                className={`w-8 h-8 ${c} rounded-lg transition-transform ${formData.color === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'}`} />
            ))}
          </div>
        </div>
        <div>
          <label className="label">Ordre d'affichage</label>
          <input type="number" value={formData.display_order}
            onChange={(e) => setFormData({ ...formData, display_order: Number(e.target.value) })}
            className="input" min={0} />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          className="rounded" />
        Visible sur le site (Ils nous font confiance)
      </label>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
        <button type="submit" className="btn-primary">{initialData ? 'Mettre à jour' : 'Ajouter'}</button>
      </div>
    </form>
  )
}


// ==================== SOUS-COMPOSANTS ====================

function NewsForm({ initialData, onSubmit, onCancel }: {
  initialData?: NewsItem | null
  onSubmit: (data: any) => void
  onCancel: () => void
}) {
  const [formData, setFormData] = useState({
    title_fr: initialData?.title_fr || initialData?.title || '',
    title_en: initialData?.title_en || '',
    excerpt_fr: initialData?.summary_fr || initialData?.excerpt_fr || initialData?.excerpt || '',
    excerpt_en: initialData?.summary_en || initialData?.excerpt_en || '',
    content_fr: initialData?.content_fr || initialData?.content || '',
    content_en: initialData?.content_en || '',
    category: normalizeCategory(initialData?.category),
    status: initialData ? (initialData.status || (initialData.is_published ? 'published' : 'draft')) : 'published',
    is_featured: initialData?.is_featured ?? false,
    image_url: initialData?.image_url || '',
  })
  const [uploading, setUploading] = useState(false)

  const handleFile = async (file: File) => {
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('title', formData.title_fr || 'Actualité')
      fd.append('description', '')
      fd.append('category', 'Actualités')
      fd.append('is_public', 'true')
      fd.append('file', file)
      const media = await contentApi.uploadImage(fd)
      setFormData(d => ({ ...d, image_url: media.image_url || media.file_path || '' }))
      toast.success('Image uploadée')
    } catch { toast.error("Erreur d'upload") } finally { setUploading(false) }
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData) }} className="p-6 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Titre (FR) *</label>
          <input required value={formData.title_fr} onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })} className="input" />
        </div>
        <div>
          <label className="label">Titre (EN)</label>
          <input value={formData.title_en} onChange={(e) => setFormData({ ...formData, title_en: e.target.value })} className="input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Catégorie</label>
          <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="input">
            {NEWS_CATEGORIES.map((c) => <option key={c} value={c}>{NEWS_CATEGORY_LABELS[c]}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Statut</label>
          <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="input">
            <option value="draft">Brouillon (invisible sur le site)</option>
            <option value="published">Publié (visible sur le site)</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Résumé (FR)</label>
          <input value={formData.excerpt_fr} onChange={(e) => setFormData({ ...formData, excerpt_fr: e.target.value })} className="input" />
        </div>
        <div>
          <label className="label">Résumé (EN)</label>
          <input value={formData.excerpt_en} onChange={(e) => setFormData({ ...formData, excerpt_en: e.target.value })} className="input" />
        </div>
      </div>
      <div>
        <label className="label">Contenu (FR) *</label>
        <textarea required rows={5} value={formData.content_fr} onChange={(e) => setFormData({ ...formData, content_fr: e.target.value })} className="input" />
      </div>
      <div>
        <label className="label">Contenu (EN)</label>
        <textarea rows={5} value={formData.content_en} onChange={(e) => setFormData({ ...formData, content_en: e.target.value })} className="input" />
      </div>
      <div>
        <label className="label">Image</label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-400 transition-colors cursor-pointer relative">
          <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
          {formData.image_url ? (
            <img src={resolveImg(formData.image_url)} alt="" className="h-32 mx-auto rounded-lg object-cover" />
          ) : (
            <>
              <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
              <p className="text-sm text-gray-500">{uploading ? 'Upload...' : 'Cliquez pour choisir une image'}</p>
            </>
          )}
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={formData.is_featured} onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })} className="rounded" />
        À la une (mis en avant)
      </label>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
        <button type="submit" className="btn-primary">{initialData ? 'Mettre à jour' : 'Créer'}</button>
      </div>
    </form>
  )
}

function EventForm({ initialData, onSubmit, onCancel }: {
  initialData?: EventItem | null
  onSubmit: (data: any) => void
  onCancel: () => void
}) {
  const rawDate = (initialData?.start_date || initialData?.event_date || '').slice(0, 10)
  const [formData, setFormData] = useState({
    title_fr: initialData?.title_fr || initialData?.title || '',
    title_en: initialData?.title_en || '',
    description_fr: initialData?.description_fr || initialData?.description || '',
    description_en: initialData?.description_en || '',
    event_type: initialData?.event_type || initialData?.category || 'Cérémonie',
    start_date: rawDate,
    location: initialData?.location || '',
    status: initialData?.status || 'published',
    is_public: initialData?.is_public ?? true,
  })

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      onSubmit({
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
      })
    }} className="p-6 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Titre (FR) *</label>
          <input required value={formData.title_fr} onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })} className="input" />
        </div>
        <div>
          <label className="label">Titre (EN)</label>
          <input value={formData.title_en} onChange={(e) => setFormData({ ...formData, title_en: e.target.value })} className="input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Type</label>
          <select value={formData.event_type} onChange={(e) => setFormData({ ...formData, event_type: e.target.value })} className="input">
            {EVENT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Date *</label>
          <input required type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Lieu</label>
          <input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="input" />
        </div>
        <div>
          <label className="label">Statut</label>
          <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="input">
            <option value="published">Publié</option>
            <option value="draft">Brouillon</option>
            <option value="cancelled">Annulé</option>
          </select>
        </div>
      </div>
      <div>
        <label className="label">Description (FR)</label>
        <textarea rows={3} value={formData.description_fr} onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })} className="input" />
      </div>
      <div>
        <label className="label">Description (EN)</label>
        <textarea rows={3} value={formData.description_en} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} className="input" />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={formData.is_public} onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })} className="rounded" />
        Visible sur le site public
      </label>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
        <button type="submit" className="btn-primary">{initialData ? 'Mettre à jour' : 'Créer'}</button>
      </div>
    </form>
  )
}

function ImageUploadForm({ onSubmit, onCancel }: {
  onSubmit: (formData: FormData) => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState(GALLERY_CATEGORIES[0])
  const [file, setFile] = useState<File | null>(null)

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      if (!file) { toast.error('Choisissez une image'); return }
      const fd = new FormData()
      fd.append('title', title || 'Photo')
      fd.append('description', description)
      fd.append('category', category)
      fd.append('is_public', 'true')
      fd.append('file', file)
      onSubmit(fd)
    }} className="p-6 space-y-4">
      <div>
        <label className="label">Image *</label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-400 transition-colors cursor-pointer relative">
          <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          {file ? (
            <img src={URL.createObjectURL(file)} alt="" className="h-32 mx-auto rounded-lg object-cover" />
          ) : (
            <>
              <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
              <p className="text-sm text-gray-500">Cliquez pour choisir une image</p>
            </>
          )}
        </div>
      </div>
      <div>
        <label className="label">Titre *</label>
        <input required value={title} onChange={(e) => setTitle(e.target.value)} className="input" />
      </div>
      <div>
        <label className="label">Description</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} className="input" />
      </div>
      <div>
        <label className="label">Catégorie</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="input">
          {GALLERY_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
        <button type="submit" className="btn-primary">Publier dans la galerie</button>
      </div>
    </form>
  )
}
